import { useAtomValue } from 'jotai'
import { motion } from 'motion/react'
import { type FormEvent, useCallback, useMemo } from 'react'
import { usePortfolioView } from '@/src/stores/portfolio-drawer/actions'
import { useEditAccountEmail } from '@/src/stores/user/actions'
import { userAtom } from '@/src/stores/user/atoms'
import { Button } from '../../ui/button'
import { Input } from '../../ui/input'

export const EditAccountEmail = () => {
  const { updateView } = usePortfolioView()
  const { mutate, isPending, isSuccess, isError } = useEditAccountEmail()
  const user = useAtomValue(userAtom)

  const onSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      event.stopPropagation()
      const formData = new FormData(event.currentTarget)

      const email = formData.get('email') as string | null

      if (email) {
        await mutate(
          {
            email,
          },
          {
            onSuccess: () => {
              updateView({
                view: 'verify-email',
              })
            },
          },
        )
      }
    },
    [mutate, updateView],
  )

  const buttonText = useMemo(() => {
    if (isPending) {
      return 'Updating...'
    }

    if (isError) {
      return 'Something went wrong, try again.'
    }

    return 'Continue'
  }, [isPending, isError])

  return (
    <motion.form
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className='flex flex-col items-center pb-10'
      onSubmit={onSubmit}
      method='POST'
    >
      <p className='text-secondary text-center mb-8'>
        Before your change is confirmed, we&apos;ll ask you to verify your new
        email address. Please ensure that only you have access to this email to
        keep your account secure.
      </p>

      <Input
        name='email'
        className='mb-4'
        dimension='medium'
        defaultValue={user?.email}
        placeholder='Enter your email'
        type='email'
        required
      />

      <Button
        type='submit'
        className='w-full'
        variant='primary'
        disabled={isPending || isSuccess}
      >
        {buttonText}
      </Button>
    </motion.form>
  )
}
